import { toast } from "sonner";
import { translateError, isNetworkError, isAuthError } from "@/lib/errorTranslation";

interface NotifyOptions {
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface PromiseMessages {
  loading: string;
  success: string;
  error?: string;
}

/**
 * useNotify - Standardized toast notification hook
 * 
 * Provides:
 * - Consistent toast styling across the app
 * - Automatic error translation for database/API errors
 * - Promise-based notifications for async operations
 * - Console logging for errors (debugging)
 * 
 * @example
 * const notify = useNotify();
 * 
 * // Simple notifications
 * notify.success("Estimate saved successfully");
 * notify.error("Failed to save estimate");
 * notify.warning("This action cannot be undone");
 * notify.info("Tip: You can drag items to reorder");
 * 
 * // With database error translation
 * try {
 *   await saveEstimate();
 *   notify.success("Saved!");
 * } catch (error) {
 *   notify.error(error); // Automatically translated to user-friendly message
 * }
 * 
 * // Promise-based (shows loading, then success/error)
 * notify.promise(saveEstimate(), {
 *   loading: "Saving estimate...",
 *   success: "Estimate saved!",
 *   error: "Failed to save estimate"
 * });
 */
export function useNotify() {
  const success = (message: string, options?: NotifyOptions) => {
    toast.success(message, {
      duration: options?.duration ?? 4000,
      action: options?.action,
    });
  };

  const error = (messageOrError: string | unknown, options?: NotifyOptions) => {
    // Handle string messages
    if (typeof messageOrError === "string") {
      toast.error(messageOrError, {
        duration: options?.duration ?? 6000,
        action: options?.action,
      });
      return;
    }

    // Handle error objects - translate to user-friendly message
    const translated = translateError(messageOrError);
    
    // Log original error to console for debugging
    console.error("Error occurred:", messageOrError);

    // Check for special error types
    if (isNetworkError(messageOrError)) {
      toast.error("Connection problem", {
        description: "Please check your internet connection and try again.",
        duration: 6000,
        action: {
          label: "Retry",
          onClick: () => window.location.reload(),
        },
      });
      return;
    }

    if (isAuthError(messageOrError)) {
      toast.error("Session expired", {
        description: "Please log in again to continue.",
        duration: 6000,
        action: {
          label: "Log in",
          onClick: () => window.location.href = "/login",
        },
      });
      return;
    }

    // Show translated error
    toast.error(translated.title, {
      description: translated.description,
      duration: options?.duration ?? 6000,
      action: options?.action,
    });
  };

  const warning = (message: string, description?: string, options?: NotifyOptions) => {
    toast.warning(message, {
      description,
      duration: options?.duration ?? 5000,
      action: options?.action,
    });
  };

  const info = (message: string, description?: string, options?: NotifyOptions) => {
    toast.info(message, {
      description,
      duration: options?.duration ?? 4000,
      action: options?.action,
    });
  };

  const promise = <T>(
    promiseOrFn: Promise<T> | (() => Promise<T>),
    messages: PromiseMessages
  ): Promise<T> => {
    const actualPromise = typeof promiseOrFn === "function" ? promiseOrFn() : promiseOrFn;

    toast.promise(actualPromise, {
      loading: messages.loading,
      success: messages.success,
      error: (err) => {
        // Log error for debugging
        console.error("Promise error:", err);
        
        // Use custom error message if provided, otherwise translate
        if (messages.error) {
          return messages.error;
        }
        const translated = translateError(err);
        return translated.description || translated.title;
      },
    });

    return actualPromise;
  };

  const dismiss = (toastId?: string | number) => {
    if (toastId) {
      toast.dismiss(toastId);
    } else {
      toast.dismiss();
    }
  };

  return {
    success,
    error,
    warning,
    info,
    promise,
    dismiss,
  };
}

// Export standalone functions for use outside of React components
export const notify = {
  success: (message: string, options?: NotifyOptions) => {
    toast.success(message, {
      duration: options?.duration ?? 4000,
      action: options?.action,
    });
  },
  error: (messageOrError: string | unknown, options?: NotifyOptions) => {
    if (typeof messageOrError === "string") {
      toast.error(messageOrError, {
        duration: options?.duration ?? 6000,
        action: options?.action,
      });
      return;
    }
    const translated = translateError(messageOrError);
    console.error("Error occurred:", messageOrError);
    toast.error(translated.title, {
      description: translated.description,
      duration: options?.duration ?? 6000,
      action: options?.action,
    });
  },
  warning: (message: string, description?: string) => {
    toast.warning(message, { description, duration: 5000 });
  },
  info: (message: string, description?: string) => {
    toast.info(message, { description, duration: 4000 });
  },
  dismiss: () => toast.dismiss(),
};
